import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { toast } from "sonner";
import {
  Receipt, Buildings, CurrencyDollar, ArrowsClockwise, FileText, ListChecks,
  ArrowSquareOut, MagnifyingGlass, Warning, CircleNotch, Link as LinkIcon, X,
  Sparkle, CheckCircle,
} from "@phosphor-icons/react";
import ZohoLinkPickerModal from "../components/billing/ZohoLinkPickerModal";

const fmt$ = (n) =>
  n == null || isNaN(n)
    ? "$0.00"
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
};

const TABS = [
  { key: "overview", label: "Overview", Icon: CurrencyDollar },
  { key: "customers", label: "Customers", Icon: Buildings },
  { key: "invoices", label: "Invoices", Icon: FileText },
  { key: "aging", label: "Aging", Icon: ListChecks },
  { key: "log", label: "Sync Log", Icon: ArrowsClockwise },
];

const STATUS_PILL = {
  current: { bg: "bg-[#2E7D32]/10", txt: "text-[#2E7D32]", label: "Current" },
  past_due: { bg: "bg-[#F57C00]/15", txt: "text-[#F57C00]", label: "Past Due" },
  overdue: { bg: "bg-[#E53935]/15", txt: "text-[#E53935]", label: "Overdue" },
  collections: { bg: "bg-[#B71C1C]/15", txt: "text-[#B71C1C]", label: "Collections" },
  unlinked: { bg: "bg-[#012340]/8", txt: "text-[#5C738A]", label: "Unlinked" },
};

function StatusPill({ status, testid }) {
  const s = STATUS_PILL[status] || STATUS_PILL.unlinked;
  return (
    <span data-testid={testid} className={`inline-flex items-center text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm ${s.bg} ${s.txt}`}>
      {s.label}
    </span>
  );
}

function MetricCard({ label, value, sub, Icon, accent }) {
  return (
    <div className="bg-white border border-[#012340]/10 rounded-sm p-5" data-testid={`billing-metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">{label}</div>
        <div className={`w-8 h-8 rounded-sm grid place-items-center ${accent ? "bg-[#CFAB7A] text-[#012340]" : "bg-[#012340] text-[#CFAB7A]"}`}>
          <Icon size={16} weight="bold" />
        </div>
      </div>
      <div className="kpi-number text-3xl mt-3 text-[#012340]">{value}</div>
      {sub && <div className="text-xs text-[#5C738A] mt-1">{sub}</div>}
    </div>
  );
}

function NotConnectedBanner({ status, onGoToIntegrations }) {
  if (status?.connected) return null;
  const msg = status?.configured
    ? "Zoho Books is configured but not yet connected. Connect from Settings → Integrations to start pulling billing data."
    : "Zoho Books is not configured yet. Add Zoho API credentials to backend/.env and connect from Settings → Integrations.";
  return (
    <div className="bg-[#012340] text-white rounded-sm border-l-4 border-[#CFAB7A] p-4 mb-5 flex items-center gap-4" data-testid="billing-not-connected">
      <div className="w-10 h-10 rounded-sm bg-[#CFAB7A]/20 text-[#CFAB7A] grid place-items-center"><Warning size={20} weight="bold" /></div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#CFAB7A]">Zoho Books — Not Connected</div>
        <div className="text-sm text-white/85 mt-1">{msg}</div>
      </div>
      <button onClick={onGoToIntegrations} className="px-4 py-2 rounded-sm bg-[#CFAB7A] text-[#012340] text-xs font-bold uppercase tracking-[0.2em] hover:brightness-105 whitespace-nowrap" data-testid="goto-integrations-btn">
        Go to Integrations
      </button>
    </div>
  );
}

function OverviewTab({ status }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/billing/overview");
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [status?.last_sync_at]);

  if (loading) return <div className="text-sm text-[#5C738A] py-10 flex items-center gap-2"><CircleNotch className="animate-spin" /> Loading overview…</div>;
  const d = data || {};
  return (
    <div className="space-y-5" data-testid="billing-overview-tab">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Open A/R" value={fmt$(d.open_ar)} sub={`${d.unpaid_invoices ?? 0} unpaid invoices`} Icon={CurrencyDollar} accent />
        <MetricCard label="Past Due" value={fmt$(d.past_due_ar)} sub="1–30 days late" Icon={Warning} />
        <MetricCard label="Overdue" value={fmt$(d.overdue_ar)} sub="31–60 days late" Icon={Warning} />
        <MetricCard label="Collections" value={fmt$(d.collections_ar)} sub="60+ days late" Icon={Warning} />
        <MetricCard label="Current A/R" value={fmt$(d.current_ar)} sub="Not yet due" Icon={Receipt} />
        <MetricCard label="Payments (This Month)" value={fmt$(d.payments_this_month)} Icon={CurrencyDollar} accent />
        <MetricCard label="Customers Past Due" value={d.customers_past_due ?? 0} Icon={Buildings} />
        <MetricCard label="Unpaid Invoices" value={d.unpaid_invoices ?? 0} Icon={FileText} />
      </div>
      <div className="bg-white border border-[#012340]/10 rounded-sm p-5 text-xs text-[#5C738A]">
        Billing data is read-only and mirrored from Zoho Books. Billing status does <span className="font-bold text-[#012340]">NOT</span> affect service routes — overdue customers stay on their routes until an admin manually pauses service.
      </div>
    </div>
  );
}

function CustomersTab({ status }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");          // all | linked | unlinked | suggested
  const [groupId, setGroupId] = useState("");
  const [routeId, setRouteId] = useState("");
  const [billingStatus, setBillingStatus] = useState("all");
  const [pickerCustomer, setPickerCustomer] = useState(null);
  const [pickerSuggestion, setPickerSuggestion] = useState(null);
  const [bulkApproving, setBulkApproving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [cR, gR, rR] = await Promise.all([
        api.get("/billing/customers"),
        api.get("/customer-groups").catch(() => ({ data: { groups: [] } })),
        api.get("/routes").catch(() => ({ data: [] })),
      ]);
      setRows(cR.data.customers || []);
      setGroups(gR.data?.groups || gR.data || []);
      setRoutes(Array.isArray(rR.data) ? rR.data : (rR.data?.routes || []));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status?.last_sync_at]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "linked" && !r.linked) return false;
      if (filter === "unlinked" && r.linked) return false;
      if (filter === "suggested" && !r.match_suggested) return false;
      if (billingStatus !== "all" && r.billing_status !== billingStatus) return false;
      if (groupId && !(r.group_ids || []).includes(groupId)) return false;
      if (routeId && !(r.route_ids || []).includes(routeId)) return false;
      if (t) {
        const haystack = `${r.business_name || ""} ${r.zoho_contact_id || ""}`.toLowerCase();
        if (!haystack.includes(t)) return false;
      }
      return true;
    });
  }, [rows, q, filter, groupId, routeId, billingStatus]);

  // Aggregate summary for the current filter set (group/broker dashboard view).
  const agg = useMemo(() => {
    const linked = filtered.filter((r) => r.linked).length;
    const unlinked = filtered.length - linked;
    const suggested = filtered.filter((r) => r.match_suggested).length;
    let openAR = 0, overdueAR = 0, totalRevenue = 0;
    for (const r of filtered) {
      openAR += r.balance || 0;
      overdueAR += r.overdue_balance || 0;
      totalRevenue += r.monthly_revenue || 0;
    }
    return { total: filtered.length, linked, unlinked, suggested, openAR, overdueAR, totalRevenue };
  }, [filtered]);

  const activeGroup = groupId ? groups.find((g) => g.id === groupId) : null;
  const activeRoute = routeId ? routes.find((r) => r.id === routeId) : null;
  const isFiltered = !!(groupId || routeId || filter !== "all" || billingStatus !== "all" || q);

  async function approveSuggestion(row) {
    if (!row.top_suggestion) return;
    try {
      await api.post(`/customers/${row.id}/zoho-link`, { zoho_contact_id: row.top_suggestion.contact_id });
      toast.success(`${row.business_name} → ${row.top_suggestion.name}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Link failed");
    }
  }

  async function ignoreSuggestion(row) {
    try {
      await api.post(`/customers/${row.id}/zoho-ignore-suggestion`);
      toast.success("Suggestion ignored");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ignore failed");
    }
  }

  async function unlink(row) {
    if (!window.confirm(`Unlink ${row.business_name} from Zoho?`)) return;
    try {
      await api.delete(`/customers/${row.id}/zoho-link`);
      toast.success("Unlinked");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unlink failed");
    }
  }

  async function refreshOne(row) {
    try {
      const { data } = await api.post(`/customers/${row.id}/zoho-refresh`);
      const s = data?.stats || {};
      toast.success(`Refreshed · ${s.invoices ?? 0} invoices · ${s.payments ?? 0} payments${s.stale_invoices_removed ? ` · ${s.stale_invoices_removed} stale removed` : ""}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refresh failed");
    }
  }

  async function bulkApproveAll() {
    const matches = filtered
      .filter((r) => !r.linked && r.match_suggested && r.top_suggestion)
      .map((r) => ({ customer_id: r.id, zoho_contact_id: r.top_suggestion.contact_id }));
    if (matches.length === 0) {
      toast.info("No suggested matches in the current filter");
      return;
    }
    if (!window.confirm(`Approve ${matches.length} suggested match${matches.length === 1 ? "" : "es"}?`)) return;
    setBulkApproving(true);
    try {
      const { data } = await api.post("/billing/bulk-approve-matches", { matches });
      toast.success(`Linked ${data.linked} of ${data.total}`);
      if (data.linked < data.total) {
        const errs = (data.results || []).filter((r) => !r.ok).slice(0, 3);
        errs.forEach((e) => toast.error(`${e.customer_id?.slice(0, 8)}…: ${e.error}`, { duration: 8000 }));
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk link failed");
    } finally {
      setBulkApproving(false);
    }
  }

  function csvEscape(v) {
    if (v == null) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }
  function downloadCsv(filename, header, rowsArr) {
    const lines = [header.join(","), ...rowsArr.map((r) => r.map(csvEscape).join(","))];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  function exportCustomerList() {
    const scope = activeGroup ? `_${activeGroup.name}` : activeRoute ? `_route-${activeRoute.route_number || activeRoute.id}` : "";
    downloadCsv(
      `zmex-customers${scope}.csv`,
      ["Business Name", "Service Status", "Zoho Link", "Zoho Contact ID", "Billing Status", "Balance", "Overdue Balance", "Monthly Revenue", "Last Invoice", "Last Payment"],
      filtered.map((r) => [
        r.business_name, r.service_status,
        r.linked ? "Linked" : r.match_suggested ? "Match Suggested" : "Not Linked",
        r.zoho_contact_id || "",
        r.billing_status, r.balance, r.overdue_balance, r.monthly_revenue,
        r.last_invoice_date || "", r.last_payment_date || "",
      ]),
    );
  }
  function exportArReport() {
    const scope = activeGroup ? `_${activeGroup.name}` : activeRoute ? `_route-${activeRoute.route_number || activeRoute.id}` : "";
    const onlyLinked = filtered.filter((r) => r.linked);
    downloadCsv(
      `zmex-ar-report${scope}.csv`,
      ["Business Name", "Zoho Contact ID", "Billing Status", "Open Balance", "Overdue Balance", "Monthly Revenue", "Last Invoice", "Last Payment"],
      onlyLinked.map((r) => [
        r.business_name, r.zoho_contact_id || "",
        r.billing_status, r.balance, r.overdue_balance, r.monthly_revenue,
        r.last_invoice_date || "", r.last_payment_date || "",
      ]),
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="billing-customers-tab">
      {/* Summary cards — clickable to filter */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 flex-shrink-0">
        <SummaryCard testid="summary-total"     label="Customers"        value={agg.total}     active={filter === "all"}       onClick={() => setFilter("all")} accent />
        <SummaryCard testid="summary-linked"    label="Linked"           value={agg.linked}    active={filter === "linked"}    onClick={() => setFilter("linked")} />
        <SummaryCard testid="summary-unlinked"  label="Unlinked"         value={agg.unlinked}  active={filter === "unlinked"}  onClick={() => setFilter("unlinked")} />
        <SummaryCard testid="summary-suggested" label="Match Suggested"  value={agg.suggested} active={filter === "suggested"} onClick={() => setFilter("suggested")} />
      </div>

      {/* Group/Broker dashboard strip — only when a group or route is selected */}
      {(activeGroup || activeRoute) && (
        <div className="bg-[#012340] text-white rounded-sm p-4 mb-4 flex-shrink-0" data-testid="group-summary-strip">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#CFAB7A]">
                {activeGroup ? "Group / Broker" : "Route"}
              </div>
              <div className="font-heading text-xl font-bold truncate">
                {activeGroup?.name || `Route ${activeRoute?.route_number || ""} ${activeRoute?.name || ""}`}
              </div>
            </div>
            <button onClick={() => { setGroupId(""); setRouteId(""); }} className="text-xs text-[#CFAB7A] hover:text-white" data-testid="clear-group-filter">
              <X size={14} weight="bold" className="inline" /> Clear
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <DarkStat label="Open A/R"          value={fmt$(agg.openAR)} />
            <DarkStat label="Overdue A/R"       value={fmt$(agg.overdueAR)} accent />
            <DarkStat label="Monthly Revenue"   value={fmt$(agg.totalRevenue)} />
            <DarkStat label="Linked / Total"    value={`${agg.linked} / ${agg.total}`} />
          </div>
        </div>
      )}

      {/* Filter toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap flex-shrink-0">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C738A]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or Zoho ID…"
            className="w-full pl-8 pr-3 py-2 rounded-sm border border-[#012340]/15 text-sm"
            data-testid="billing-customers-search"
          />
        </div>

        <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className="px-3 py-2 rounded-sm border border-[#012340]/15 text-sm bg-white min-w-[160px]" data-testid="billing-filter-group">
          <option value="">All Groups / Brokers</option>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}{g.customer_count != null ? ` (${g.customer_count})` : ""}</option>)}
        </select>

        <select value={routeId} onChange={(e) => setRouteId(e.target.value)} className="px-3 py-2 rounded-sm border border-[#012340]/15 text-sm bg-white min-w-[140px]" data-testid="billing-filter-route">
          <option value="">All Routes</option>
          {routes.map((r) => <option key={r.id} value={r.id}>{r.route_number || r.id}{r.name ? ` — ${r.name}` : ""}</option>)}
        </select>

        <select value={billingStatus} onChange={(e) => setBillingStatus(e.target.value)} className="px-3 py-2 rounded-sm border border-[#012340]/15 text-sm bg-white" data-testid="billing-filter-status">
          <option value="all">All Billing Status</option>
          <option value="current">Current</option>
          <option value="past_due">Past Due</option>
          <option value="overdue">Overdue</option>
          <option value="collections">Collections</option>
          <option value="unlinked">Unlinked</option>
        </select>

        {isFiltered && (
          <button onClick={() => { setQ(""); setFilter("all"); setGroupId(""); setRouteId(""); setBillingStatus("all"); }} className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A] hover:text-[#012340]" data-testid="clear-all-filters">
            Clear all
          </button>
        )}

        <div className="ml-auto text-xs text-[#5C738A]">{filtered.length} of {rows.length}</div>
      </div>

      {/* Bulk actions */}
      <div className="flex items-center gap-2 mb-3 flex-wrap flex-shrink-0">
        <button
          onClick={bulkApproveAll}
          disabled={bulkApproving || agg.suggested === 0 || !status?.connected}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#CFAB7A] text-[#012340] text-[10px] font-bold uppercase tracking-[0.2em] hover:brightness-105 disabled:opacity-40"
          data-testid="bulk-approve-btn"
          title={!status?.connected ? "Connect Zoho first" : ""}
        >
          <Sparkle size={12} weight="bold" /> {bulkApproving ? "Linking…" : `Bulk Approve (${agg.suggested})`}
        </button>
        <button
          onClick={exportCustomerList}
          disabled={filtered.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-[#012340]/15 text-[#012340] text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#FAFAFA] disabled:opacity-40"
          data-testid="export-customers-btn"
        >
          Export Customer List
        </button>
        <button
          onClick={exportArReport}
          disabled={agg.linked === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-[#012340]/15 text-[#012340] text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#FAFAFA] disabled:opacity-40"
          data-testid="export-ar-btn"
        >
          Export A/R Report
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#012340]/10 rounded-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="overflow-auto flex-1 min-h-0">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#FAFAFA] border-b border-[#012340]/10 z-10">
              <tr>
                <Th>Customer</Th>
                <Th>Zoho Status</Th>
                <Th>Billing Status</Th>
                <Th align="right">Balance</Th>
                <Th align="right">Overdue</Th>
                <Th>Last Invoice</Th>
                <Th>Last Payment</Th>
                <Th align="right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#012340]/5">
              {loading && (
                <tr><td colSpan={8} className="py-10 text-center text-sm text-[#5C738A]"><CircleNotch className="inline animate-spin mr-2" /> Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="py-10 text-center text-sm text-[#5C738A]">No customers match the current filter.</td></tr>
              )}
              {!loading && filtered.map((r) => (
                <tr key={r.id} data-testid={`billing-customer-row-${r.id}`} className="hover:bg-[#FAFAFA]/60">
                  <Td>
                    <div className="font-semibold text-[#012340] truncate max-w-[240px]" title={r.business_name}>{r.business_name}</div>
                    {r.zoho_contact_id && <div className="text-[10px] font-mono text-[#5C738A]">Zoho ID: {r.zoho_contact_id}</div>}
                  </Td>
                  <Td><ZohoStatusPill row={r} /></Td>
                  <Td><StatusPill status={r.billing_status} /></Td>
                  <Td align="right" className="font-mono">{r.linked ? fmt$(r.balance) : "—"}</Td>
                  <Td align="right" className={`font-mono ${r.overdue_balance > 0 ? "text-[#E53935] font-bold" : ""}`}>{r.linked ? fmt$(r.overdue_balance) : "—"}</Td>
                  <Td className="text-xs text-[#5C738A]">{fmtDate(r.last_invoice_date)}</Td>
                  <Td className="text-xs text-[#5C738A]">{fmtDate(r.last_payment_date)}</Td>
                  <Td align="right">
                    <RowActions
                      row={r}
                      onApprove={() => approveSuggestion(r)}
                      onIgnore={() => ignoreSuggestion(r)}
                      onManual={() => { setPickerCustomer({ id: r.id, business_name: r.business_name }); setPickerSuggestion(r.top_suggestion || null); }}
                      onUnlink={() => unlink(r)}
                      onRefresh={() => refreshOne(r)}
                      zohoConnected={!!status?.connected}
                    />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ZohoLinkPickerModal
        open={!!pickerCustomer}
        customer={pickerCustomer}
        initialSuggestion={pickerSuggestion}
        onClose={() => { setPickerCustomer(null); setPickerSuggestion(null); }}
        onLinked={() => load()}
      />
    </div>
  );
}

function SummaryCard({ label, value, active, onClick, accent, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left bg-white border rounded-sm p-4 transition-colors ${active ? "border-[#CFAB7A] ring-1 ring-[#CFAB7A]/40" : "border-[#012340]/10 hover:border-[#012340]/30"}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">{label}</div>
        {accent && <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#CFAB7A]">All</span>}
      </div>
      <div className="kpi-number text-3xl text-[#012340] mt-2">{value}</div>
    </button>
  );
}
function DarkStat({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#CFAB7A]">{label}</div>
      <div className={`font-mono text-lg mt-0.5 ${accent ? "text-[#FF9A6B]" : ""}`}>{value}</div>
    </div>
  );
}
function ZohoStatusPill({ row }) {
  if (row.linked) return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm bg-[#2E7D32]/12 text-[#2E7D32]" data-testid="zoho-pill-linked"><CheckCircle size={10} weight="bold" /> Linked</span>;
  if (row.match_suggested) return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm bg-[#CFAB7A]/30 text-[#012340]" data-testid="zoho-pill-suggested"><Sparkle size={10} weight="bold" /> Match Suggested</span>;
  return <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm bg-[#012340]/8 text-[#5C738A]" data-testid="zoho-pill-unlinked">Not Linked</span>;
}
function RowActions({ row, onApprove, onIgnore, onManual, onUnlink, onRefresh, zohoConnected }) {
  if (!zohoConnected) {
    return <span className="text-[10px] text-[#5C738A] italic">Connect Zoho</span>;
  }
  if (row.linked) {
    return (
      <div className="inline-flex items-center gap-1.5">
        <button
          onClick={onRefresh}
          className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#012340] hover:text-[#CFAB7A]"
          data-testid={`row-refresh-${row.id}`}
          title="Force live refresh from Zoho"
        >
          Refresh
        </button>
        <span className="text-[#012340]/20">|</span>
        <button onClick={onUnlink} className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E53935] hover:underline" data-testid={`row-unlink-${row.id}`}>
          Unlink
        </button>
      </div>
    );
  }
  if (row.match_suggested) {
    return (
      <div className="inline-flex items-center gap-1.5">
        <button
          onClick={onApprove}
          className="text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-sm bg-[#012340] text-white hover:bg-[#012340]/90"
          data-testid={`row-approve-${row.id}`}
          title={row.top_suggestion ? `${row.top_suggestion.name} · ${Math.round((row.top_suggestion.score || 0) * 100)}%` : ""}
        >
          Approve
        </button>
        <button onClick={onManual} className="text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-sm border border-[#012340]/15 text-[#012340] hover:bg-[#FAFAFA]" data-testid={`row-manual-${row.id}`}>
          Manual
        </button>
        <button onClick={onIgnore} className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A] hover:text-[#012340]" data-testid={`row-ignore-${row.id}`}>
          Ignore
        </button>
      </div>
    );
  }
  return (
    <button onClick={onManual} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-1 rounded-sm bg-[#CFAB7A] text-[#012340] hover:brightness-105" data-testid={`row-link-${row.id}`}>
      <LinkIcon size={10} weight="bold" /> Link Zoho Customer
    </button>
  );
}

function InvoicesTab({ status }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = statusFilter !== "all" ? { status: statusFilter } : {};
        const { data } = await api.get("/billing/invoices", { params });
        if (!cancelled) setRows(data.invoices || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [statusFilter, status?.last_sync_at]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) =>
      (r.invoice_number || "").toLowerCase().includes(t) ||
      (r.customer_name || "").toLowerCase().includes(t)
    );
  }, [rows, q]);

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="billing-invoices-tab">
      <div className="flex items-center gap-2 mb-3 flex-wrap flex-shrink-0">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C738A]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search invoice # or customer…"
            className="w-full pl-8 pr-3 py-2 rounded-sm border border-[#012340]/15 text-sm"
            data-testid="billing-invoices-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-sm border border-[#012340]/15 text-sm"
          data-testid="billing-invoices-status-filter"
        >
          <option value="all">All Statuses</option>
          <option value="sent">Sent</option>
          <option value="paid">Paid</option>
          <option value="partially_paid">Partially Paid</option>
          <option value="overdue">Overdue</option>
          <option value="draft">Draft</option>
        </select>
        <div className="ml-auto text-xs text-[#5C738A]">{filtered.length} invoices</div>
      </div>

      <div className="bg-white border border-[#012340]/10 rounded-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="overflow-auto flex-1 min-h-0">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#FAFAFA] border-b border-[#012340]/10 z-10">
              <tr>
                <Th>Invoice #</Th>
                <Th>Customer</Th>
                <Th>Date</Th>
                <Th>Due</Th>
                <Th>Status</Th>
                <Th align="right">Total</Th>
                <Th align="right">Balance</Th>
                <Th align="right">Days Past Due</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#012340]/5">
              {loading && (
                <tr><td colSpan={8} className="py-10 text-center text-sm text-[#5C738A]"><CircleNotch className="inline animate-spin mr-2" /> Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="py-10 text-center text-sm text-[#5C738A]">No invoices to display{!status?.connected ? " — connect Zoho Books to pull invoice data." : "."}</td></tr>
              )}
              {!loading && filtered.map((r) => (
                <tr key={r.invoice_id} data-testid={`billing-invoice-row-${r.invoice_id}`}>
                  <Td><span className="font-mono text-[#012340] font-semibold">{r.invoice_number || r.invoice_id}</span></Td>
                  <Td><div className="truncate max-w-[240px]">{r.customer_name || "—"}</div></Td>
                  <Td className="text-xs text-[#5C738A]">{fmtDate(r.date)}</Td>
                  <Td className="text-xs text-[#5C738A]">{fmtDate(r.due_date)}</Td>
                  <Td><span className="text-xs uppercase tracking-[0.15em] font-bold text-[#012340]">{r.status || "—"}</span></Td>
                  <Td align="right" className="font-mono">{fmt$(r.total)}</Td>
                  <Td align="right" className="font-mono">{fmt$(r.balance)}</Td>
                  <Td align="right">
                    <span className={r.days_past_due > 0 ? "text-[#E53935] font-bold" : "text-[#5C738A]"}>
                      {r.days_past_due > 0 ? `${r.days_past_due}d` : "—"}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AgingTab({ status }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/billing/aging");
        if (!cancelled) setData(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [status?.last_sync_at]);

  if (loading) return <div className="text-sm text-[#5C738A] py-10 flex items-center gap-2"><CircleNotch className="animate-spin" /> Loading aging…</div>;
  const buckets = data?.buckets || [];
  const totalAR = buckets.reduce((s, b) => s + b.amount, 0);

  return (
    <div className="space-y-5" data-testid="billing-aging-tab">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {buckets.map((b) => (
          <MetricCard
            key={b.key}
            label={b.label}
            value={fmt$(b.amount)}
            sub={`${b.invoice_count} inv · ${b.customer_count} cust`}
            Icon={Receipt}
            accent={b.key === "current"}
          />
        ))}
      </div>
      <div className="bg-white border border-[#012340]/10 rounded-sm p-5">
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#5C738A] mb-3">Aging Distribution</div>
        <div className="space-y-2">
          {buckets.map((b) => {
            const pct = totalAR > 0 ? Math.round((b.amount / totalAR) * 100) : 0;
            return (
              <div key={b.key} data-testid={`aging-bar-${b.key}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-bold text-[#012340]">{b.label}</span>
                  <span className="text-[#5C738A] font-mono">{fmt$(b.amount)} · {pct}%</span>
                </div>
                <div className="h-2 bg-[#012340]/8 rounded-sm overflow-hidden">
                  <div className="h-full bg-[#CFAB7A]" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SyncLogTab({ status }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/integrations/zoho/sync-log");
        if (!cancelled) setLogs(data.logs || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [status?.last_sync_at]);

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="billing-log-tab">
      <div className="bg-white border border-[#012340]/10 rounded-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="overflow-auto flex-1 min-h-0">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#FAFAFA] border-b border-[#012340]/10 z-10">
              <tr>
                <Th>Started</Th>
                <Th>Completed</Th>
                <Th>Status</Th>
                <Th>Contacts</Th>
                <Th>Invoices</Th>
                <Th>Payments</Th>
                <Th>Error</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#012340]/5">
              {loading && (
                <tr><td colSpan={7} className="py-10 text-center text-sm text-[#5C738A]"><CircleNotch className="inline animate-spin mr-2" /> Loading…</td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={7} className="py-10 text-center text-sm text-[#5C738A]">No sync events yet.</td></tr>
              )}
              {!loading && logs.map((l) => (
                <tr key={l.id} data-testid={`sync-log-row-${l.id}`}>
                  <Td className="font-mono text-xs">{l.started_at ? new Date(l.started_at).toLocaleString() : "—"}</Td>
                  <Td className="font-mono text-xs">{l.completed_at ? new Date(l.completed_at).toLocaleString() : "—"}</Td>
                  <Td>
                    <span className={`text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm ${
                      l.status === "success" ? "bg-[#2E7D32]/10 text-[#2E7D32]" :
                      l.status === "failed" ? "bg-[#E53935]/15 text-[#E53935]" : "bg-[#012340]/8 text-[#5C738A]"
                    }`}>{l.status}</span>
                  </Td>
                  <Td className="font-mono">{l.stats?.contacts ?? 0}</Td>
                  <Td className="font-mono">{l.stats?.invoices ?? 0}</Td>
                  <Td className="font-mono">{l.stats?.payments ?? 0}</Td>
                  <Td className="text-xs text-[#E53935] truncate max-w-[260px]">{l.error || ""}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`px-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A] text-${align}`}>{children}</th>;
}
function Td({ children, align = "left", className = "" }) {
  return <td className={`px-4 py-3 text-${align} ${className}`}>{children}</td>;
}

export default function BillingHub() {
  const [tab, setTab] = useState("overview");
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  async function loadStatus() {
    try {
      const { data } = await api.get("/integrations/zoho/status");
      setStatus(data);
    } catch {
      setStatus({ configured: false, connected: false });
    }
  }
  useEffect(() => { loadStatus(); }, []);

  async function runSync() {
    setSyncing(true);
    try {
      await api.post("/integrations/zoho/sync");
      toast.success("Zoho sync completed");
      await loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="billing-hub">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5 flex-shrink-0">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#5C738A]">Billing</div>
          <h1 className="font-heading text-3xl font-black text-[#012340] mt-1">Billing Hub</h1>
          <p className="text-sm text-[#5C738A] mt-1">Read-only view of Zoho Books — A/R, invoices, customer balances. Billing status is decoupled from service routing.</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {status?.connected && (
            <button
              onClick={runSync}
              disabled={syncing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#012340] text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#012340]/90 disabled:opacity-50"
              data-testid="billing-sync-btn"
            >
              <ArrowsClockwise size={14} weight="bold" className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing…" : "Sync Now"}
            </button>
          )}
          <a
            href="https://books.zoho.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#012340] hover:text-[#CFAB7A]"
            data-testid="open-zoho-link"
          >
            Open Zoho Books <ArrowSquareOut size={14} weight="bold" />
          </a>
        </div>
      </div>

      <NotConnectedBanner status={status} onGoToIntegrations={() => (window.location.href = "/admin/integrations")} />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#012340]/10 mb-5 overflow-x-auto flex-shrink-0">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] border-b-2 -mb-px whitespace-nowrap ${
              tab === key ? "border-[#CFAB7A] text-[#012340]" : "border-transparent text-[#5C738A] hover:text-[#012340]"
            }`}
            data-testid={`billing-tab-${key}`}
          >
            <Icon size={14} weight="bold" /> {label}
          </button>
        ))}
      </div>

      {/* Tab Body — flex-1 owns the remaining height; each tab manages its own scroll. */}
      <div className="flex-1 min-h-0">
        {tab === "overview" && <OverviewTab status={status} />}
        {tab === "customers" && <CustomersTab status={status} />}
        {tab === "invoices" && <InvoicesTab status={status} />}
        {tab === "aging" && <AgingTab status={status} />}
        {tab === "log" && <SyncLogTab status={status} />}
      </div>
    </div>
  );
}
